/**
 * Sandbox Checklist
 *
 * Read-only authoring status list: each item shows its title, description, any
 * produced artifacts, and a status icon. No actions — the agent drives the
 * checklist; this just reports where it's at. Flat, hairline-divided rows to
 * match the rest of the sandbox sidebar.
 *
 * @module features/sandboxes/components/SandboxChecklist
 */

import React from 'react';
import { Text } from '@chatsdet/ui';
import {
  CheckCircle,
  Circle,
  CircleNotch,
  File,
  ListChecks,
  XCircle,
} from '@phosphor-icons/react';
import { useSandboxChecklist } from '../hooks/useSandboxChecklist';
import type { ChecklistItemWithStatus } from '@chatsdet/types';

interface SandboxChecklistProps {
  templateId: string;
}

/** Status glyph — completed / failed / everything-else (pending). Running and
 * skipped collapse to the neutral pending dot: this view reports status, it
 * doesn't expose the agent's in-flight work. */
function StatusIcon({ status }: { status: string }) {
  if (status === 'completed') {
    return <CheckCircle size={18} weight="fill" className="text-emerald-500 shrink-0" />;
  }
  if (status === 'failed') {
    return <XCircle size={18} weight="fill" className="text-red-500 shrink-0" />;
  }
  return <Circle size={18} className="text-muted-foreground/30 shrink-0" />;
}

function ChecklistRow({ item }: { item: ChecklistItemWithStatus }) {
  const { itemStatus } = item;
  const isDone = itemStatus.status === 'completed';
  const isFailed = itemStatus.status === 'failed';

  // Produced artifacts (only the ones that actually landed), shown as a quiet
  // sub-line under the description.
  const producedArtifacts = item.producesArtifacts
    .map((type) => item.artifacts.find((a) => a.artifactType === type))
    .filter((a): a is NonNullable<typeof a> => Boolean(a));

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <span className="mt-0.5">
        <StatusIcon status={itemStatus.status} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Text size="sm" weight="medium" className={isDone ? '' : 'text-foreground'}>
            {item.label}
          </Text>
          {item.countsTowardVerification === false && (
            <Text size="xs" className="text-muted-foreground/60">
              optional
            </Text>
          )}
        </div>
        <Text size="xs" className="text-muted-foreground mt-0.5">
          {item.description}
        </Text>
        {producedArtifacts.length > 0 && (
          <div className="mt-1.5 flex flex-col gap-0.5">
            {producedArtifacts.map((artifact) => (
              <div key={artifact.filename} className="flex items-center gap-1.5">
                <File size={12} className="text-muted-foreground/50 shrink-0" />
                <Text size="xs" className="text-muted-foreground/80 truncate">
                  {artifact.filename}
                </Text>
              </div>
            ))}
          </div>
        )}
        {isFailed && itemStatus.error && (
          <Text size="xs" className="text-red-500 mt-1 line-clamp-2">
            {itemStatus.error}
          </Text>
        )}
      </div>
    </div>
  );
}

export const SandboxChecklist: React.FC<SandboxChecklistProps> = ({ templateId }) => {
  const { items, isLoading } = useSandboxChecklist(templateId);

  const completedCount = items.filter(
    (i) => i.itemStatus.status === 'completed' || i.itemStatus.status === 'skipped'
  ).length;

  return (
    <div>
      <div className="flex items-center gap-2 h-11 px-4 border-b border-border">
        <ListChecks size={14} weight="bold" className="text-muted-foreground shrink-0" />
        <Text size="sm" weight="semibold" className="flex-1">
          Checklist
        </Text>
        {items.length > 0 && (
          <Text size="xs" className="text-muted-foreground tabular-nums">
            {completedCount}/{items.length}
          </Text>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <CircleNotch size={20} className="animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="divide-y divide-border">
          {items.map((item) => (
            <ChecklistRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
};
